import { useState, useEffect, useRef, useCallback } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useFamily } from '../context/FamilyContext';
import { subscribeToCustodySchedules, subscribeToCustodyOverrides } from '@coparent/shared/src/services/custodyService';
import { subscribeToActivities } from '@coparent/shared/src/services/activityService';
import { getAccessToken, findOrCreateCoparentCalendar, clearToken, hasValidToken } from '../services/googleCalendarService';
import { fullSync, removeAllSyncedEvents } from '../services/googleCalendarSyncService';
import toast from 'react-hot-toast';

const useGoogleCalendarSync = () => {
  const { currentUser, userData, refreshUserData } = useAuth();
  const { family, familyId } = useFamily();

  const [isSyncing, setIsSyncing] = useState(false);
  const [needsReconnect, setNeedsReconnect] = useState(false);

  // Live data refs for sync
  const schedulesRef = useRef([]);
  const overridesRef = useRef([]);
  const activitiesRef = useRef([]);
  const debounceTimerRef = useRef(null);

  const gcalPrefs = userData?.googleCalendar || {};
  const isConnected = gcalPrefs.enabled === true;
  const syncMode = gcalPrefs.syncMode || 'both';
  const calendarId = gcalPrefs.calendarId || null;
  const lastSyncAt = gcalPrefs.lastSyncAt || null;

  // Subscribe to data changes for auto-sync
  useEffect(() => {
    if (!familyId || !isConnected) return;

    const unsub1 = subscribeToCustodySchedules(familyId, (data) => {
      schedulesRef.current = data;
      triggerAutoSync();
    });
    const unsub2 = subscribeToCustodyOverrides(familyId, (data) => {
      overridesRef.current = data;
      triggerAutoSync();
    });
    const unsub3 = subscribeToActivities(familyId, (data) => {
      activitiesRef.current = data;
      triggerAutoSync();
    });

    return () => { unsub1(); unsub2(); unsub3(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId, isConnected]);

  const triggerAutoSync = useCallback(() => {
    if (!isConnected || !calendarId) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      performSync(false);
    }, 2000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, calendarId, syncMode]);

  const performSync = async (showToast = true) => {
    if (isSyncing || !currentUser || !family || !calendarId) return;

    // Check if we have a valid token before trying
    if (!hasValidToken()) {
      try {
        await getAccessToken(false);
      } catch {
        setNeedsReconnect(true);
        return;
      }
    }

    setIsSyncing(true);
    setNeedsReconnect(false);

    try {
      await fullSync(currentUser.uid, calendarId, syncMode, {
        schedules: schedulesRef.current,
        overrides: overridesRef.current,
        family,
        activities: activitiesRef.current,
      });

      await updateDoc(doc(db, 'users', currentUser.uid), {
        'googleCalendar.lastSyncAt': new Date().toISOString(),
      });

      if (showToast) {
        toast.success('Google Calendar synced');
      }
    } catch (err) {
      console.error('Google Calendar sync failed:', err);
      if (err.message.includes('403') || err.message.includes('401')) {
        setNeedsReconnect(true);
        if (showToast) {
          toast.error('Google Calendar access was revoked. Please reconnect.');
        }
      } else if (showToast) {
        toast.error('Sync failed. Please try again.');
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const connect = async () => {
    if (!currentUser) return;

    try {
      // Trigger consent popup
      await getAccessToken(true);

      // Find or create the coparent calendar
      const newCalendarId = await findOrCreateCoparentCalendar();

      // Save preferences to Firestore
      await updateDoc(doc(db, 'users', currentUser.uid), {
        googleCalendar: {
          enabled: true,
          syncMode: 'both',
          calendarId: newCalendarId,
          lastSyncAt: null,
        },
      });

      await refreshUserData();
      setNeedsReconnect(false);
      toast.success('Google Calendar connected');

      // Trigger initial sync after a short delay for state to update
      setTimeout(() => performSync(true), 500);
    } catch (err) {
      console.error('Failed to connect Google Calendar:', err);
      if (err.message !== 'popup_closed_by_user') {
        toast.error('Failed to connect Google Calendar');
      }
    }
  };

  const disconnect = async () => {
    if (!currentUser) return;

    setIsSyncing(true);
    try {
      if (calendarId) {
        await removeAllSyncedEvents(currentUser.uid, calendarId);
      }

      clearToken();

      await updateDoc(doc(db, 'users', currentUser.uid), {
        googleCalendar: {
          enabled: false,
          syncMode: 'both',
          calendarId: null,
          lastSyncAt: null,
        },
      });

      await refreshUserData();
      toast.success('Google Calendar disconnected');
    } catch (err) {
      console.error('Failed to disconnect Google Calendar:', err);
      toast.error('Failed to disconnect. Please try again.');
    } finally {
      setIsSyncing(false);
    }
  };

  const setSyncMode = async (newMode) => {
    if (!currentUser) return;

    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'googleCalendar.syncMode': newMode,
      });
      await refreshUserData();
    } catch (err) {
      console.error('Failed to update sync mode:', err);
      toast.error('Failed to update sync preference');
    }
  };

  const syncNow = () => performSync(true);

  const reconnect = async () => {
    try {
      await getAccessToken(true);
      setNeedsReconnect(false);
      toast.success('Reconnected to Google Calendar');
      performSync(true);
    } catch (err) {
      if (err.message !== 'popup_closed_by_user') {
        toast.error('Failed to reconnect');
      }
    }
  };

  return {
    isConnected,
    syncMode,
    isSyncing,
    lastSyncAt,
    needsReconnect,
    connect,
    disconnect,
    syncNow,
    setSyncMode,
    reconnect,
  };
};

export default useGoogleCalendarSync;
